import styled, { css } from 'styled-components';
import { NoResultContainer } from '../NoResult/style';

export const StyledModal = styled.dialog.withConfig({
  shouldForwardProp: (prop) => !['paddingInline'].includes(prop),
})`
  position: fixed;
  top: 0;
  left: 0;
  flex-direction: column;
  border: none;
  border-radius: 4px;
  padding: 0 ${({ paddingInline }) => `${paddingInline}px`} 0;
  width: 658px;
  transition: ${(props) => props.theme.constants.transitionBase};

  &[open] {
    display: flex;
  }

  /* min-margin */
  &:modal {
    max-width: calc(100% - 48px * 2);
    max-height: calc(100% - 50px * 2);
    width: fit-content;
  }

  &.max-size:modal {
    min-width: calc(min(1440px, 100%) - 48px * 2);
    max-width: calc(min(1440px, 100%) - 48px * 2);
    min-height: calc(90vh - 50px * 2);
    width: fit-content;
  }

  &::backdrop {
    background: rgba(40, 40, 40, 0.5);
  }

  /* mobile */
  @media ${(props) => props.theme.device.mobileL} {
    padding: 0;
  }

  &.fullscreen {
    border: none;
    border-radius: 0;
    width: var(--full-width);
    height: 100vh;
    margin: 0;

    &:modal {
      max-width: var(--full-width);
      max-height: 100vh;
    }
  }

  &.compact {
    @media ${(props) => props.theme.device.mobileL} {
      margin: auto 16px;
      width: calc(100% - 16px * 2);
      max-width: 100%;
    }
  }

  &.overflow-x-hidden {
    overflow-x: hidden;
  }
`;

export const Header = styled.div`
  z-index: 1;
  flex-shrink: 0;
  position: -webkit-sticky;
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  padding-top: 40px;
  background-color: ${({ theme }) => theme.colors.white};

  @media ${(props) => props.theme.device.mobileL} {
    padding: 24px 16px 0;
  }
`;

export const TitleWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  column-gap: 24px; /* min-gap */

  ${StyledModal}.is-survey & .h1-5 {
    --close-btn-plus-gap-size: 100px;
    text-align: center;
    margin-right: var(--close-btn-plus-gap-size);
    transform: translateX(var(--close-btn-plus-gap-size));

    @media ${(props) => props.theme.device.mobileL} {
      margin-right: calc(var(--close-btn-plus-gap-size) * -1);
      margin-top: 48px;
      transform: none;
    }
  }
`;

export const ScrollableArea = styled.div.withConfig({
  shouldForwardProp: (prop) => !['preserveShadows'].includes(prop),
})`
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 200px; //? ensure it's visible even on a smaller vertical viewport

  &.has-scrollbar {
    padding-right: 16px;
  }

  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-track {
    background-color: ${({ theme }) => theme.colors.scrollbarTrack};
    border-radius: 99px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.scrollbarThumb};
    border-radius: 99px;
  }

  ${({ preserveShadows }) =>
    preserveShadows &&
    css`
      padding-inline: 16px;
      margin-inline: -16px;
      //padding-top: 16px;
      //margin-top: -16px;

      &::-webkit-scrollbar-track {
        //margin-top: 16px;
        margin-bottom: 16px;
      }

      > *:last-child:not(${NoResultContainer}) {
        margin-bottom: 16px;
      }
    `}

  ${StyledModal}.fullscreen & {
    padding-bottom: 24px;

    &::-webkit-scrollbar-track {
      margin-bottom: 24px;
    }

    /* when there is no footer (buttons) */
    &:last-child {
      padding-bottom: 0;

      &::-webkit-scrollbar-track {
        margin-bottom: 0;
      }
    }
  }

  &.fullwidth {
    width: calc(100% + 16px * 2);
    margin-left: -16px;
  }
  &.auto-height {
    height: auto !important;
  }

  @media ${(props) => props.theme.device.mobileL} {
    height: 100%;
    padding: 0 16px;
    max-height: 100%;
  }
`;

export const Footer = styled.div.withConfig({
  shouldForwardProp: (prop) => !['pt', 'pb'].includes(prop),
})`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  column-gap: 40px;
  position: -webkit-sticky;
  position: sticky;
  bottom: 0;
  left: 0;
  padding-top: ${({ pt }) => pt ?? 32}px;
  padding-bottom: ${({ pb }) => pb ?? 32}px;
  background-color: ${({ theme }) => theme.colors.white};

  width: calc(100% + 48px * 2);
  margin-left: -48px;
  box-shadow: 4px 6px 60px 0px rgba(4, 86, 179, 0.1);

  /* mobile */
  @media ${(props) => props.theme.device.mobileL} {
    flex-direction: column-reverse;
    row-gap: 8px;
    width: 100%;
    margin-left: 0;

    button {
      width: 100%;
    }
  }

  ${StyledModal}.is-survey & {
    width: calc(100% + 32px * 2);
    margin-left: -32px;

    @media ${(props) => props.theme.device.mobileL} {
      flex-direction: row;
      column-gap: 32px;
      width: 100%;
      margin-left: 0;
    }
  }

  ${StyledModal}.fullscreen & {
    padding: 24px 16px;
    box-shadow: 0px 0px 20px 0px #6868681a;
    margin: auto 0 0; /* compensate for modal padding */
  }

  ${StyledModal}.compact & {
    box-shadow: none;
    padding: 64px 40px 40px;

    @media ${(props) => props.theme.device.mobileL} {
      padding: 48px 16px 24px;
    }
  }

  /* for when the paginator doesn't show !(number_of_pages > 1) */
  &:empty {
    padding: 0; /* discard pt/pb values */
    margin-bottom: 40px; /* same as padding-top of modal */
  }
`;

export const PagContainer = styled.div`
  > div {
    padding-bottom: 8px;
  }
`;

export const FiltersWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 24px;

  @media ${(props) => props.theme.device.tabletL} {
    justify-content: space-between;
    flex-wrap: wrap;
  }
`;
